<script setup>
import { computed } from 'vue'
import { Head, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  prefix:   { type: String, default: '' },
  featured: { type: Object, default: () => ({ vehicles: [], tours: [], properties: [] }) },
})

const { t } = useI18n()
const page     = usePage()
const channels = computed(() => page.props.brand?.channels || {})
const writeUsChannels = computed(() => channels.value.message || [])
const followUsChannels = computed(() => channels.value.social_page || [])

// Bold gradient promo cards (the eye-catching centrepiece). Each links into
// its catalog; property routes to the partner sign-up since it's pre-launch.
const promos = computed(() => [
  { key: 'rentals',  mod: 'rentals',  href: `${props.prefix}/rentals`,  cta: t('public.hero_cta') },
  { key: 'tours',    mod: 'tours',    href: `${props.prefix}/tours`,    cta: t('public.home.promo.browse_tours') },
  { key: 'property', mod: 'property', href: `${props.prefix}/partners`, cta: t('public.home.list_place') },
])

function platformLabel(p) {
  return p.charAt(0).toUpperCase() + p.slice(1)
}
function urlHint(url) {
  if (!url) return ''
  try {
    const u = new URL(url)
    const pathTail = u.pathname.replace(/\/+$/, '').split('/').pop() || u.hostname
    return pathTail.startsWith('@') ? pathTail : `${u.hostname.replace(/^www\./, '')}${u.pathname}`.replace(/\/+$/, '')
  } catch {
    return url
  }
}
</script>

<template>
  <Head>
    <title>{{ t('public.head_title') }}</title>
    <meta name="description" :content="t('public.head_description')">
    <meta property="og:title"       :content="t('public.head_title')">
    <meta property="og:description" :content="t('public.head_description')">
    <meta property="og:type"        content="website">
  </Head>

  <!-- HERO — full-bleed coral band with the sun motif -->
  <section class="hero">
    <svg class="hero__sun" viewBox="0 0 120 120" aria-hidden="true">
      <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
        <line x1="60" y1="6" x2="60" y2="30" /><line x1="60" y1="90" x2="60" y2="114" />
        <line x1="6" y1="60" x2="30" y2="60" /><line x1="90" y1="60" x2="114" y2="60" />
        <line x1="22" y1="22" x2="38" y2="38" /><line x1="82" y1="82" x2="98" y2="98" />
        <line x1="98" y1="22" x2="82" y2="38" /><line x1="38" y1="82" x2="22" y2="98" />
      </g>
      <circle cx="60" cy="60" r="22" fill="currentColor" />
    </svg>
    <div class="hero__inner">
      <p class="hero__eyebrow">{{ t('public.eyebrow') }}</p>
      <h1 class="hero__title">{{ t('public.headline') }}</h1>
      <p class="hero__lede">{{ t('public.lede') }}</p>
      <div class="hero__actions">
        <a :href="`${props.prefix}/rentals`" class="btn btn--light">{{ t('public.hero_cta') }} →</a>
        <a href="#explore" class="btn btn--ghost">{{ t('public.cards.explore') }} ↓</a>
      </div>
    </div>
  </section>

  <!-- FEATURE PROMO CARDS — bold gradient, eye-catching -->
  <section id="explore" class="features">
    <div class="features__inner">
      <a v-for="p in promos" :key="p.key" :href="p.href" :class="['promo', `promo--${p.mod}`]">
        <svg class="promo__sun" viewBox="0 0 120 120" aria-hidden="true">
          <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
            <line x1="60" y1="6" x2="60" y2="30" /><line x1="60" y1="90" x2="60" y2="114" />
            <line x1="6" y1="60" x2="30" y2="60" /><line x1="90" y1="60" x2="114" y2="60" />
            <line x1="22" y1="22" x2="38" y2="38" /><line x1="82" y1="82" x2="98" y2="98" />
            <line x1="98" y1="22" x2="82" y2="38" /><line x1="38" y1="82" x2="22" y2="98" />
          </g>
          <circle cx="60" cy="60" r="22" fill="currentColor" />
        </svg>
        <div class="promo__content">
          <p class="promo__eyebrow">{{ t(`public.pillars.${p.key}`) }}</p>
          <h2 class="promo__head">{{ t(`public.home.promo.${p.key}_head`) }}</h2>
          <p class="promo__sub">{{ t(`public.home.promo.${p.key}_sub`) }}</p>
          <span class="promo__cta">{{ p.cta }} →</span>
        </div>
      </a>
    </div>
  </section>

  <!-- PRODUCT RAILS — one swipe row per section -->
  <section class="rails">
    <div class="rails__inner">
      <ProductRail
        :title="t('public.pillars.rentals')"
        kind="vehicle"
        :items="featured.vehicles"
        :item-base="`${props.prefix}/rentals`"
        :view-all-href="`${props.prefix}/rentals`"
      />
      <ProductRail
        :title="t('public.pillars.tours')"
        kind="tour"
        :items="featured.tours"
        :item-base="`${props.prefix}/tours`"
        :view-all-href="`${props.prefix}/tours`"
      />
    </div>
  </section>

  <!-- CONTACT — dark Ink band -->
  <section v-if="writeUsChannels.length || followUsChannels.length" class="contact">
    <div class="contact__inner">
      <h2 class="contact__title">{{ t('public.cta_lede') }}</h2>
      <div v-if="writeUsChannels.length" class="contact__channels">
        <a
          v-for="c in writeUsChannels"
          :key="c.id"
          class="chan"
          :href="c.url"
          target="_blank"
          rel="noopener"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="chan__icon">
            <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4z" />
          </svg>
          <span class="chan__text">
            <span class="chan__name">{{ t('public.write_on', { platform: platformLabel(c.platform) }) }}</span>
            <span class="chan__handle">{{ c.label || urlHint(c.url) }}</span>
          </span>
        </a>
      </div>
      <template v-if="followUsChannels.length">
        <p class="contact__follow-label">{{ t('public.follow_us') }}</p>
        <div class="contact__follow">
          <a
            v-for="c in followUsChannels"
            :key="c.id"
            class="follow"
            :href="c.url"
            target="_blank"
            rel="noopener"
          >{{ platformLabel(c.platform) }}</a>
        </div>
      </template>
    </div>
  </section>

  <!-- PARTNER band -->
  <section class="partner">
    <div class="partner__inner">
      <span class="partner__text">{{ t('public.partners.home_band_text') }}</span>
      <a :href="`${props.prefix}/partners`" class="partner__link">{{ t('public.partners.home_band_cta') }}</a>
    </div>
  </section>
</template>

<style scoped>
.hero, .features, .rails, .contact, .partner { width: 100%; }

/* Shared wide inner — fills the page on large screens. */
.hero__inner, .features__inner, .rails__inner, .contact__inner, .partner__inner {
  max-width: 96rem;
  margin: 0 auto;
  padding-inline: clamp(1.25rem, 4vw, 3rem);
}

/* ---- HERO ---------------------------------------------------------------- */
.hero {
  position: relative;
  overflow: hidden;
  color: var(--accentInk);
  background:
    radial-gradient(120% 130% at 82% 12%, color-mix(in oklch, var(--accent) 78%, white) 0%, var(--accent) 46%, color-mix(in oklch, var(--accent) 78%, black) 100%);
}
.hero__sun {
  position: absolute;
  top: -8rem; right: -4rem;
  width: 34rem; height: 34rem;
  color: var(--accentInk);
  opacity: 0.14;
  pointer-events: none;
}
.hero__inner { padding-top: clamp(4rem, 11vw, 9rem); padding-bottom: clamp(3.5rem, 9vw, 7rem); position: relative; }
.hero__eyebrow {
  font-size: 0.8125rem; letter-spacing: 0.22em; text-transform: uppercase;
  font-weight: 600; margin: 0 0 1.25rem; opacity: 0.92;
}
.hero__title {
  font-family: var(--font-display, var(--display));
  font-weight: 800; font-size: clamp(2.75rem, 7.5vw, 5.5rem);
  line-height: 1.0; letter-spacing: -0.03em; margin: 0; max-width: 18ch;
}
.hero__lede {
  font-size: clamp(1.15rem, 2vw, 1.5rem); line-height: 1.5;
  margin: 1.5rem 0 0; max-width: 46ch; opacity: 0.95;
}
.hero__actions { display: flex; flex-wrap: wrap; gap: 0.875rem; margin-top: 2.5rem; }
.btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 1rem 1.75rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1.1rem;
  text-decoration: none; transition: transform 150ms ease, background 150ms ease;
}
.btn--light { background: var(--surface); color: var(--accent); }
.btn--light:hover { transform: translateY(-2px); }
.btn--ghost {
  background: color-mix(in oklch, var(--accentInk) 14%, transparent);
  color: var(--accentInk); border: 1px solid color-mix(in oklch, var(--accentInk) 45%, transparent);
}
.btn--ghost:hover { background: color-mix(in oklch, var(--accentInk) 22%, transparent); }

/* ---- FEATURE PROMO CARDS ------------------------------------------------- */
.features__inner {
  padding-top: clamp(2.5rem, 6vw, 4.5rem); padding-bottom: clamp(1rem, 3vw, 2rem);
  display: grid; grid-template-columns: repeat(auto-fit, minmax(19rem, 1fr)); gap: 1.5rem;
}
.promo {
  position: relative; overflow: hidden;
  min-height: 23rem; border-radius: 1.75rem;
  display: flex; align-items: flex-end; padding: 2.25rem;
  text-decoration: none; isolation: isolate;
  transition: transform 180ms ease, box-shadow 180ms ease;
}
.promo:hover { transform: translateY(-5px); box-shadow: 0 26px 50px -24px color-mix(in oklch, var(--promo-shadow, var(--ink)) 60%, transparent); }
.promo__sun {
  position: absolute; top: -5rem; right: -4rem; width: 20rem; height: 20rem;
  color: currentColor; opacity: 0.16; pointer-events: none; z-index: -1;
}
.promo__content { color: inherit; }
.promo__eyebrow {
  font-size: 0.8rem; letter-spacing: 0.2em; text-transform: uppercase;
  font-weight: 700; margin: 0 0 0.6rem; opacity: 0.85;
}
.promo__head {
  font-family: var(--font-display, var(--display)); font-weight: 800;
  font-size: clamp(1.9rem, 3vw, 2.5rem); line-height: 1.04; letter-spacing: -0.02em; margin: 0;
}
.promo__sub { margin: 0.7rem 0 1.4rem; font-size: 1.1rem; line-height: 1.45; opacity: 0.94; max-width: 26ch; }
.promo__cta {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.8rem 1.4rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1.05rem;
}

/* Rentals — coral gradient, dark pill */
.promo--rentals {
  color: var(--accentInk); --promo-shadow: var(--accent);
  background: radial-gradient(120% 130% at 82% 12%, color-mix(in oklch, var(--accent) 80%, white) 0%, var(--accent) 48%, color-mix(in oklch, var(--accent) 75%, black) 100%);
}
.promo--rentals .promo__cta { background: var(--ink); color: var(--accentInk); }

/* Tours — lagoon gradient, dark pill */
.promo--tours {
  color: #fff; --promo-shadow: var(--second);
  background: radial-gradient(120% 130% at 82% 12%, color-mix(in oklch, var(--second) 78%, white) 0%, var(--second) 48%, color-mix(in oklch, var(--second) 72%, black) 100%);
}
.promo--tours .promo__cta { background: var(--ink); color: #fff; }

/* Property — ink gradient, coral pill */
.promo--property {
  color: var(--bg); --promo-shadow: var(--ink);
  background: linear-gradient(155deg, color-mix(in oklch, var(--ink) 92%, var(--second)) 0%, var(--ink) 70%);
}
.promo--property .promo__cta { background: var(--accent); color: var(--accentInk); }

/* ---- PRODUCT RAILS ------------------------------------------------------- */
.rails { background: var(--bg); }
.rails__inner { padding-top: clamp(2rem, 4vw, 3rem); padding-bottom: clamp(3rem, 6vw, 5rem); }

/* ---- CONTACT (Ink band) -------------------------------------------------- */
.contact { background: var(--ink); color: var(--bg); }
.contact__inner { padding-block: clamp(3rem, 7vw, 5rem); }
.contact__title {
  font-family: var(--font-display, var(--display)); font-weight: 800;
  font-size: clamp(1.9rem, 4vw, 2.75rem); letter-spacing: -0.02em; margin: 0 0 1.75rem;
}
.contact__channels { display: grid; grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr)); gap: 0.875rem; }
.chan {
  display: flex; align-items: center; gap: 0.875rem; padding: 1.15rem 1.3rem;
  border-radius: 1rem; background: var(--accent); color: var(--accentInk);
  text-decoration: none; transition: transform 150ms ease;
}
.chan:hover { transform: translateY(-2px); }
.chan__icon { width: 1.5rem; height: 1.5rem; flex: none; }
.chan__text { display: flex; flex-direction: column; }
.chan__name { font-weight: 700; font-size: 1.1rem; }
.chan__handle { font-size: 0.92rem; opacity: 0.85; margin-top: 0.1rem; }
.contact__follow-label { margin: 2.25rem 0 0.875rem; font-size: 1.05rem; opacity: 0.7; }
.contact__follow { display: flex; flex-wrap: wrap; gap: 0.625rem; }
.follow {
  padding: 0.65rem 1.2rem; border-radius: 999px;
  border: 1px solid color-mix(in oklch, var(--bg) 35%, transparent);
  color: var(--bg); text-decoration: none; font-weight: 600; font-size: 1rem;
  transition: background 150ms ease;
}
.follow:hover { background: color-mix(in oklch, var(--bg) 12%, transparent); }

/* ---- PARTNER band -------------------------------------------------------- */
.partner { background: var(--surfaceAlt); }
.partner__inner {
  padding-block: clamp(2rem, 4vw, 3rem);
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 0.6rem 1rem; text-align: center;
}
.partner__text { font-size: 1.15rem; color: var(--inkSoft); }
.partner__link {
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1.15rem;
  color: var(--accent); text-decoration: none;
}
.partner__link:hover { text-decoration: underline; }
</style>
